import { useState, useEffect, useMemo } from 'react'
import { useFormikContext } from 'formik'
import styles from '../CustomDomainModal.module.css'
import { DNS_RECORDS, VALIDATE_RECORDS, VALIDATE_RECORDS_DIAGNOSTICS } from '../constants/strings'
import DNSTable from './DNSTable'
import { Button, Spinner } from 'react-bootstrap'
import {
  autoConnectDomainFinish,
  autoConnectDomainRetry,
  publishSiteToCustomDomain,
  fetchNameServers
} from '../../../services/custom-domain'
import { detectRegistrar } from '../../../utils/registrarMatcher'
import { domainErrorMessage } from '../../CustomDomainTable'
import { API_ROOT } from 'constants'
import api from 'utils/api'
import { useCustomDomain } from '../context/CustomDomainProvider'

const RegistrarHelpBox = ({ info }) => {
  if (!info) return null
  return (
    <div className={styles.registrarHelpBox}>
      Your domain is managed by <strong>{info.registrarName}</strong>.{' '}
      <a href={info.helpArticleUrl} target="_blank" rel="noreferrer">
        {info.helpArticleName}
      </a>
    </div>
  )
}

const DNSRecords = ({
  onNext,
  onSelectEntri,
  updateDomains,
  refreshProfile,
  onFail,
  setPublishSuccessData,
  userProfile,
  handleClose,
  modalData,
  variant = 'standard',
  isPublishFlow = false,
  onCAAError
}) => {
  const [loading, setLoading] = useState(false)
  const { isPublishToCustomDomain, setIsPublishToCustomDomain } = useCustomDomain()
  const { DNSRecordsDiagnostics, setDNSRecordsDiagnostics } = useCustomDomain()
  const [isDiagnosticsError, setIsDiagnosticsError] = useState(false)
  const [registrarInfo, setRegistrarInfo] = useState(null)

  // Use Formik context instead of prop
  const { values, setFieldValue } = useFormikContext()

  const dnsData = values.dnsRecords.data
  const validate = localStorage.getItem('validate')
  const retry = localStorage.getItem('retry')
  const domain = values.dnsAddress.domain
  const subdomain = values.dnsAddress.subdomain
  const fulldomainAddress = !!subdomain ? `${subdomain}.${domain}` : domain
  const canSetUpAutomatically =
    values.wizard.selected === 'no' &&
    !!dnsData?.domain &&
    !!dnsData?.token &&
    !!dnsData?.cert?.name &&
    !!dnsData?.cert?.value &&
    !!dnsData?.cfName &&
    !!onSelectEntri

  const isPhpVariant = variant === 'php'

  const isDiagnosticsMismatch = (data) => data && !data?.success && data?.records?.length

  const VALIDATE_RECORDS_CONSTANTS = useMemo(
    () => (isDiagnosticsMismatch(DNSRecordsDiagnostics) ? VALIDATE_RECORDS_DIAGNOSTICS : VALIDATE_RECORDS),
    [DNSRecordsDiagnostics]
  )

  useEffect(() => {
    if (domain) {
      fetchNameServers(domain).then((nameServers) => {
        const info = detectRegistrar(nameServers)
        if (info) {
          setRegistrarInfo(info)
        }
      })
    }
  }, [domain])

  useEffect(() => {
    if (modalData) {
      setFieldValue(`dnsAddress.domain`, modalData?.domain)
      setFieldValue(`dnsRecords.data`, modalData)
      setFieldValue(`dnsAddress.subdomain`, modalData?.subdomain)
    } else if (!!validate && !isPhpVariant) {
      // Only for standard variant
      const data = JSON.parse(validate)
      setFieldValue(`dnsAddress.domain`, data?.domain)
      setFieldValue(`dnsRecords.data`, data)
      setFieldValue(`dnsAddress.subdomain`, data?.subdomain)
    }
  }, [validate, modalData, isPhpVariant, setFieldValue])

  const updateSitePublishToCustomDomain = async (site) => {
    let splitSubdomain = site.publishToCustomDomain?.split('.')
    let subdomain = site.subdomain || splitSubdomain[0]
    let tld = `.${site.domain}`

    const formData = new FormData()
    formData.append('newDomain', `${subdomain}${tld}`)
    formData.append('publishToCustomDomain', site.publishToCustomDomain)

    setIsPublishToCustomDomain(true)

    return await publishSiteToCustomDomain({
      currentSiteDomain: site.publishToCustomDomain,
      formData
    })
  }

  const handleNext = async () => {
    const createdTime = localStorage.getItem('start_time')
    if (!createdTime) localStorage.setItem('start_time', new Date())
    localStorage.setItem('form_values', JSON.stringify(values))

    setLoading(true)
    try {
      await autoConnectDomainRetry({ domain: fulldomainAddress, registrar: registrarInfo?.registrarName })
      if (refreshProfile) {
        refreshProfile()
      }
    } catch (error) {
      console.error('Failed to confirm domain setup:', error)
    } finally {
      setLoading(false)
    }

    onNext()
  }

  const handleRetry = async () => {
    setLoading(true)
    try {
      if (!!fulldomainAddress) {
        const {
          data: { success }
        } = await autoConnectDomainRetry({ domain: fulldomainAddress, registrar: registrarInfo?.registrarName })
        if (success) {
          refreshProfile()
          localStorage.setItem('start_time', dnsData?.created)
          onNext()
        }
      }
      setLoading(false)
    } catch (error) {
      const errorCode = error?.response?.data?.code
      if (errorCode === 'ACM_CERT_STATUS_FAILED' || errorCode === 'ACM_STATUS_FAILED') {
        setLoading(false)
        onCAAError?.({
          domain: fulldomainAddress,
          originalDomain: domain,
          subdomain,
          isRootDomain: !subdomain,
          modalData: dnsData
        })
        return
      }
      setLoading(false)
      onFail(domainErrorMessage)
    }
  }

  const onValidate = async () => {
    setLoading(true)
    setDNSRecordsDiagnostics(null)
    try {
      if (!!domain) {
        const result = await autoConnectDomainFinish({ domain: domain })
        const {
          data: { domains, success }
        } = result
        if (success) {
          const siteToPublish = localStorage.getItem('publishToCustomDomain')
          if (siteToPublish) {
            const publishSite = domains.find((d) => d.domain === domain && d.publishToCustomDomain === siteToPublish)

            if (publishSite) {
              try {
                const response = await updateSitePublishToCustomDomain(publishSite)
                if (response.data) {
                  setPublishSuccessData({
                    site: response.data?.link || {},
                    files: response.data?.files || [],
                    userProfile: userProfile
                  })
                }
              } catch (error) {
                console.error('Failed to publish site to custom domain:', error)
              } finally {
                localStorage.removeItem('publishToCustomDomain')
                handleClose()
                return
              }
            }
          } else {
            updateDomains(domains)
            setFieldValue(`isSetUpComplete`, true)
            refreshProfile()
          }
        } else {
          setFieldValue(`validationFailed`, true)
        }
      }
      setLoading(false)
    } catch (error) {
      const errorCode = error?.response?.data?.code
      if (errorCode === 'ACM_CERT_STATUS_FAILED' || errorCode === 'ACM_STATUS_FAILED') {
        setLoading(false)
        onCAAError?.({
          domain: fulldomainAddress,
          originalDomain: domain,
          subdomain,
          isRootDomain: !subdomain,
          modalData: dnsData
        })
        return
      }
      setFieldValue(`validationFailed`, true)
      setLoading(false)
    }
    onNext()
  }

  const onRunDiagnostics = async () => {
    setLoading(true)
    setDNSRecordsDiagnostics(null)
    try {
      const validateOrRetry = modalData || (validate && JSON.parse(validate)) || (retry && JSON.parse(retry))
      if (!validateOrRetry) {
        setLoading(false)
        return
      }

      // Skip diagnostics for Enom domains and directly validate
      if (validateOrRetry.isEnomDomain) {
        onValidate()
        return
      }

      const diagnostics = await api.post(`${API_ROOT}/v3/custom-domain/diagnostics`, {
        domain,
        dnsData: validateOrRetry
      })
      const results = diagnostics?.data

      // Check if the CAA record specifically failed — trigger the CAA error modal immediately
      const caaFailed = results?.records?.find((r) => r.type === 'CAA' && !r.matches)
      if (caaFailed) {
        setLoading(false)
        onCAAError?.({
          domain: fulldomainAddress,
          originalDomain: domain,
          subdomain,
          isRootDomain: !subdomain,
          modalData: dnsData
        })
        return
      }

      if (isDiagnosticsMismatch(results)) {
        setDNSRecordsDiagnostics({ ...results, records: results?.records?.filter((r) => !r.matches) })
        setLoading(false)
      } else if (!isDiagnosticsMismatch(results) && results?.message) {
        setDNSRecordsDiagnostics({ message: results?.message, success: results?.success })
        setIsDiagnosticsError(true)
        setLoading(false)
      } else {
        onValidate()
      }
    } catch (error) {
      setDNSRecordsDiagnostics({ error })
      setIsDiagnosticsError(true)
      setLoading(false)
    }
  }

  const handleViewSite = () => {
    window.open(`https://${fulldomainAddress}`, '_blank')
  }

  const handleAutomaticSetup = () => {
    const { domain: entriDomain, token, cert, cfName } = dnsData

    setFieldValue('wizard.selected', 'yes')
    onSelectEntri(entriDomain, token, cert, cfName, !subdomain, dnsData)
  }

  // PHP Variant Render
  if (isPhpVariant) {
    return (
      <div className={styles.container}>
        <div className={styles.subHeading}>
          <div className={styles.title}>{DNS_RECORDS.SUBHEADING}</div>
          {!modalData && <div className={styles.subTitle}>{DNS_RECORDS.DESCRIPTION}</div>}
        </div>
        {!modalData && <RegistrarHelpBox info={registrarInfo} />}
        <div className={styles.recordsCon}>
          <DNSTable data={dnsData} domain={domain} />
        </div>

        {!modalData ? (
          <div className={styles.btnContainerRight}>
            <Button onClick={onNext} size="small" className={styles.forwardButton}>
              Done
            </Button>
          </div>
        ) : (
          <div className={styles.domainStatusBox}>
            <div className={styles.domainName}>{fulldomainAddress}</div>
            <Button onClick={handleViewSite} variant="primary" size="small">
              View Site
            </Button>
          </div>
        )}
      </div>
    )
  }

  // Standard Variant Render
  return (
    <div className={styles.container}>
      {!!validate ? (
        <>
          <div className={styles.subHeading}>
            <div className={styles.title}>
              {loading ? VALIDATE_RECORDS_CONSTANTS.UPLOADING : VALIDATE_RECORDS_CONSTANTS.SUBHEADING}
            </div>
            <div className={styles.subTitle}>
              {loading ? VALIDATE_RECORDS_CONSTANTS.UPLOADING_DESC : VALIDATE_RECORDS_CONSTANTS.DESCRIPTION}
            </div>
          </div>
          {!loading && <RegistrarHelpBox info={registrarInfo} />}
          {!loading && (
            <div className={styles.recordsCon}>
              <DNSTable data={dnsData} domain={domain} />
            </div>
          )}

          <div className={`${styles.btnContainer} ${!!validate && !loading ? styles.right : styles.center}`}>
            {loading ? (
              <Spinner animation="border" />
            ) : (
              !isDiagnosticsError && (
                <Button onClick={onRunDiagnostics} size="small" className={styles.forwardButton}>
                  Validate
                </Button>
              )
            )}
          </div>
        </>
      ) : (
        <>
          <div className={styles.subHeading}>
            <div className={styles.title}>{DNS_RECORDS.SUBHEADING}</div>
            <div className={styles.subTitle}>{DNS_RECORDS.DESCRIPTION}</div>
          </div>
          {canSetUpAutomatically && (
            <div>
              <Button variant="outline-primary" size="sm" onClick={handleAutomaticSetup}>
                Set up automatically instead
              </Button>
            </div>
          )}
          <RegistrarHelpBox info={registrarInfo} />
          <div className={styles.recordsCon}>
            <DNSTable data={dnsData} domain={domain} />
          </div>
          <div className={styles.checkbox}>
            <input
              type="checkbox"
              checked={values.dnsRecords.checked}
              onChange={() => setFieldValue(`dnsRecords.checked`, !values.dnsRecords.checked)}
            />
            <span className={styles.subTitle}>I have added the above DNS records</span>
          </div>
          <div className={`${styles.btnContainer} ${styles.right}`}>
            <Button
              onClick={!!retry ? handleRetry : handleNext}
              size="small"
              disabled={!values.dnsRecords.checked}
              className={styles.forwardButton}
              loading={loading}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default DNSRecords
